import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { BadgeCheck, Clock3, Loader2, XCircle } from 'lucide-react';
import MarketplaceFooter from '../components/marketplace/MarketplaceFooter';
import { useLegalSettings } from '../hooks/useLegalSettings';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '') + '/api';

export default function MarketplaceProlongarPage() {
  const legal = useLegalSettings() || {};
  const contactEmail = legal.email_geral || '';
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [duracao, setDuracao] = useState(30);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null); // { nova_expiracao }

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/marketplace/prolongar/info/${token}`);
        if (active) setInfo(res.data);
      } catch (e) {
        if (active) setErr(e.response?.data?.detail || 'Link inválido ou expirado.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    setErr('');
    try {
      const res = await axios.post(`${API_BASE}/marketplace/prolongar/${token}`, {
        duracao_dias: duracao,
      });
      setDone({ nova_expiracao: res.data.nova_expiracao });
    } catch (e) {
      setErr(e.response?.data?.detail || 'Erro ao prolongar.');
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString('pt-PT', { day: '2-digit', month: 'long', year: 'numeric' })
      : '—';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" data-testid="prolongar-page">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex w-full max-w-6xl items-center gap-3 px-4 py-4">
          <img src="/icon-192x192.png" alt="Marketplace TVDE" className="h-10 w-10 rounded-xl object-cover ring-1 ring-slate-200" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Marketplace TVDE</p>
            <h1 className="text-lg font-extrabold text-slate-900">Prolongar Anúncio</h1>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl px-4 py-12">
        {loading && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm" data-testid="prolongar-loading">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-emerald-500" />
            <p className="mt-3 text-sm text-slate-600">A carregar informações do anúncio…</p>
          </div>
        )}

        {!loading && err && !done && (
          <div className="rounded-2xl border border-red-200 bg-white p-8 shadow-sm" data-testid="prolongar-error">
            <div className="flex items-start gap-3">
              <XCircle className="h-7 w-7 shrink-0 text-red-500" />
              <div>
                <h2 className="text-lg font-bold text-slate-900">Link inválido ou expirado</h2>
                <p className="mt-1 text-sm text-slate-600">{err}</p>
                <p className="mt-3 text-sm text-slate-500">
                  Se precisa de prolongar o seu anúncio, contacte-nos por{' '}
                  <a href={`mailto:${contactEmail}`} className="font-semibold text-emerald-600 underline" data-testid="prolongar-contact-email">
                    {contactEmail}
                  </a>{' '}
                  ou peça um novo link à administração.
                </p>
                <Link
                  to="/marketplace"
                  className="mt-4 inline-block rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  ← Voltar ao Marketplace
                </Link>
              </div>
            </div>
          </div>
        )}

        {!loading && info && !done && (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8" data-testid="prolongar-card">
            <div className="flex items-start gap-3">
              <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
                <Clock3 className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-extrabold text-slate-900" data-testid="prolongar-title">
                  Olá {info.primeiro_nome || 'Anunciante'} 👋
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Quer prolongar a publicação do seu anúncio no Marketplace TVDE?
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Anúncio</p>
              <p className="mt-1 text-base font-bold text-slate-900" data-testid="prolongar-listing-title">{info.titulo}</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {info.tipo === 'viatura' ? 'Viatura' : 'Motorista'} · {info.cidade}
              </p>
              <p className="mt-2 text-xs text-slate-500">
                Expirava em <strong>{fmt(info.expires_at_atual)}</strong>
              </p>
            </div>

            <p className="mt-6 text-sm font-semibold text-slate-700">Escolha a duração do prolongamento:</p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {[15, 30].map((d) => (
                <button
                  key={d}
                  onClick={() => setDuracao(d)}
                  className={`rounded-xl border px-4 py-3 text-base font-bold transition ${duracao === d ? 'border-emerald-500 bg-emerald-500 text-white shadow-md' : 'border-slate-200 bg-white text-slate-700 hover:border-emerald-300'}`}
                  data-testid={`prolongar-option-${d}`}
                >
                  + {d} dias
                </button>
              ))}
            </div>

            {err && (
              <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="prolongar-submit-error">
                {err}
              </p>
            )}

            <button
              onClick={confirm}
              disabled={submitting}
              className="mt-5 w-full rounded-xl bg-emerald-500 px-4 py-3 text-base font-bold text-white shadow-sm transition hover:bg-emerald-600 disabled:opacity-60"
              data-testid="prolongar-confirm"
            >
              {submitting ? 'A prolongar…' : `Confirmar prolongamento de ${duracao} dias`}
            </button>

            <p className="mt-3 text-center text-xs text-slate-500">
              O anúncio voltará a ficar visível imediatamente após confirmar.
            </p>
          </div>
        )}

        {done && (
          <div className="rounded-2xl border border-emerald-200 bg-white p-8 shadow-sm" data-testid="prolongar-success">
            <div className="flex items-start gap-3">
              <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200">
                <BadgeCheck className="h-6 w-6" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-extrabold text-slate-900">Anúncio prolongado!</h2>
                <p className="mt-2 text-sm text-slate-700">
                  O seu anúncio volta a estar visível no Marketplace TVDE até{' '}
                  <strong className="text-emerald-700">{fmt(done.nova_expiracao)}</strong>.
                </p>
                <Link
                  to="/marketplace"
                  className="mt-5 inline-block rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-600"
                  data-testid="prolongar-back-to-marketplace"
                >
                  Ver Marketplace →
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>

      <MarketplaceFooter />
    </div>
  );
}
